<template>
  <template v-for="(node, index) in schema.children" :key="childKeys[index]">
    <!-- Force-hide override (editor preview only) short-circuits the whole node,
         so a hidden SharedNodeRef can't fall through to the normal-component
         branch and be mis-rendered via its registry entry. -->
    <template v-if="visibilityOverrides[node.nodeId] !== 'hide'">
      <!-- SharedNodeRef: resolved and rendered via SharedNodeRefRenderer which provides slot context -->
      <SharedNodeRefRenderer
        v-if="node.type === 'SharedNodeRef'"
        :node="node"
        :ComponentRegistry="ComponentRegistry"
        :editable="editable"
        @update:schema-fields="(id, val) => emit('update:schema-fields', id, val)"
      />

      <!-- ComponentSlot: render slot fillers when inside a shared node, else show placeholder.
         Slot fillers are user content placed on the SharedNodeRef instance — their
         editability follows the editability the enclosing ref received (via the
         `outerEditable` injection from SharedNodeRefRenderer), NOT the SNR-forced
         readonly template context. -->
      <RenderComponents
        v-else-if="node.type === 'ComponentSlot' && hasSlotFillers(node)"
        :schema="{ children: getSlotFillers(node) }"
        :ComponentRegistry="ComponentRegistry"
        :editable="outerEditable"
        :cascade="slotCascade(index)"
        @update:schema-fields="(id, val) => emit('update:schema-fields', id, val)"
      />

      <!-- Normal component rendering.
         ComponentSlot is an authoring marker (`allowChildren: []`) — at runtime
         outside editor mode it has no content to render, which produces a
         component with no root element and triggers Vue's "extraneous non-props
         attributes … component renders fragment" warning when `data-id` etc.
         fall through. Skip instantiation in non-editor modes; the editor still
         shows the dashed placeholder. -->
      <template v-else>
        <!-- Always present, and inert (`css: false`, no hooks) for a node with
             no animation — Vue then resolves the leave synchronously, so an
             unanimated node unmounts exactly as it did before. -->
        <!-- Crawlable <a href> around a node whose click goes to a known page
             (built pipe only; see PipeNavLink). Deliberately NOT gated on
             nodeShown: swapping the wrapper would remount the Transition and
             drop the enter/leave motion of a rule-gated node. A hidden node
             leaves an empty, box-less anchor instead. -->
        <PipeNavLink :page-id="getStaticClickTarget(node.events)">
          <Transition v-bind="transitionFor(node, cascade, cascadeIndex(index))">
            <component
              v-if="
                ComponentRegistry[node.type] &&
                nodeShown(node) &&
                !(node.type === 'ComponentSlot' && !isEditorMode)
              "
              v-node-animation="directiveTarget(node, cascade, cascadeIndex(index))"
              :is="ComponentRegistry[node.type]!.component"
              :class="classesFor(node, cascade, cascadeIndex(index))"
              :style="stylesFor(node, cascade, cascadeIndex(index))"
              :fields="node.fields"
              :slotChildren="hasSlottedChildren(node) ? groupChildrenBySlot(node) : undefined"
              :editable="editable"
              @update:fields="
                (value: ComponentNode['fields']) => {
                  emit('update:schema-fields', node.nodeId, value)
                }
              "
              @update:schema-fields="
                (nodeId: string, value: ComponentNode['fields']) =>
                  emit('update:schema-fields', nodeId, value)
              "
              :data-has-events="'true'"
              :data-id="node.nodeId"
              :data-type="node.type"
              :data-slot="node.slot || undefined"
              :data-shared-internal="sharedNodeInternal ? sharedNodeRefId : undefined"
              :data-slot-target="
                sharedNodeInternal && node.type === 'ComponentSlot' ? sharedNodeRefId : undefined
              "
              v-on="getEventListeners(node.events || {}, node.nodeId)"
            >
              <template v-if="node.children && !hasSlottedChildren(node)">
                <RenderComponents
                  :schema="{ children: node.children }"
                  :ComponentRegistry="ComponentRegistry"
                  :editable="editable"
                  :shared-node-internal="sharedNodeInternal"
                  :cascade="cascadeFor(node, cascade, cascadeIndex(index))"
                  @update:schema-fields="(id, val) => emit('update:schema-fields', id, val)"
                />
              </template>
            </component>
          </Transition>
        </PipeNavLink>
      </template>
    </template>
  </template>
</template>

<script setup lang="ts">
import type { ComponentSchema, ComponentNode } from '../api/types.gen'
import RenderComponents from './RenderComponents.vue'
import SharedNodeRefRenderer from './SharedNodeRefRenderer.vue'
import PipeNavLink from './PipeNavLink.vue'
import { getStaticClickTarget } from './lib/linkExtractor'
import { PAGE_FORM_GROUP_KEY, PipeDisplayMode, usePipe } from './composables/usePipe'
import { useVisibilityMap } from './composables/useLogic'
import { usePipeEventHandlers } from './composables/usePipeEventHandlers'
import { useNodeAnimation, type AnimationCascade } from './composables/useNodeAnimation'
import { siblingRenderKeys } from './lib/siblingRenderKeys'
import { computed, inject, provide } from 'vue'
import type { ComputedRef } from 'vue'
import '@render/assets/node-animation.css'
import '@render/assets/max-width-align.css'

const pipe = usePipe()
const isEditorMode = computed(() => pipe.currentMode.value === PipeDisplayMode.EDITOR)
const { getEventListeners } = usePipeEventHandlers(pipe)

const emit = defineEmits<{
  (e: 'update:schema-fields', nodeId: string, value: ComponentNode['fields']): void
}>()

const props = defineProps<{
  schema: ComponentSchema
  ComponentRegistry: Record<string, { component: unknown }>
  // Governs TipTap / content editability — threaded through the whole render tree,
  // including layout components' slots (via RenderSlot + Column/Carousel/TabView).
  editable?: boolean
  // Flags direct children of a SharedNodeRef's rendered template — used for the
  // `data-shared-internal` attribute (useEditorEvents click/drag detection) and
  // propagated through the recursive direct-children branch. Not threaded through
  // slotted layout components (same as pre-refactor).
  sharedNodeInternal?: boolean
  // What the enclosing node's `animation.staggerChildren` hands these children
  // (useNodeAnimation's `cascadeFor`). Set on the recursive direct-children
  // branch and passed on to slot fillers: a slotted layout component renders its
  // children through RenderSlot, which never sees the owning node, so stagger
  // does not reach those.
  cascade?: AnimationCascade
}>()

const sharedNodeRefId = inject<string | null>('sharedNodeRefId', null)

// Provide editability context — disabled for shared node internals.
const contentEditable = computed(() => !props.sharedNodeInternal && !!props.editable)
provide('contentEditable', contentEditable)

// Provide sharedNodeInternal so RenderSlot (used by layout components) can
// propagate it to its inner RenderComponents — ensures data-shared-internal
// attribute is set on components inside layout slots within a SharedNodeRef.
provide(
  'sharedNodeInternal',
  computed(() => !!props.sharedNodeInternal),
)

// Provide ComponentRegistry so RenderSlot can access it via inject
provide('renderComponentRegistry', props.ComponentRegistry)

// Form-group scope — defaults to the page-implicit sentinel when no `<Form>`
// ancestor has provided a group key. A nested `<Form>` shadows this with its
// own per-instance key. Children call `inject('formGroupKey')` (e.g. via
// `useFormInput`) to discover the nearest form group.
const injectedFormGroupKey = inject<ComputedRef<string | null> | null>('formGroupKey', null)
provide(
  'formGroupKey',
  computed(() => injectedFormGroupKey?.value ?? PAGE_FORM_GROUP_KEY),
)

// Enter/leave motion configured on the node (`node.animation`), independent of
// whether it has visibility rules — see useNodeAnimation.ts.
const { classesFor, stylesFor, transitionFor, cascadeFor, directiveTarget, vNodeAnimation } =
  useNodeAnimation()

/** The cascade for the fillers of the ComponentSlot at `index`: they take its place in the stagger. */
function slotCascade(index: number): AnimationCascade | undefined {
  const cascade = props.cascade
  return cascade && { ...cascade, delay: cascade.delay + cascade.step * cascadeIndex(index) }
}

const disableAllVisibilityRules = computed(() => !!pipe.disableAllVisibilityRules.value)
const visibilityMap = useVisibilityMap(() => props.schema, pipe.getData)

// Editor-only per-node force show/hide overrides (empty in a real build). The
// outer template guard already excludes 'hide'; this adds the 'show' force and
// keeps the existing rules logic for everything else.
// `useSlotHasVisibleChild` re-derives this gate so a layout component can drop
// an empty band — keep the two in step.
const visibilityOverrides = computed(() => pipe.visibilityOverrides.value)
function nodeShown(node: ComponentNode): boolean {
  if (visibilityOverrides.value[node.nodeId] === 'show') return true
  return disableAllVisibilityRules.value || !!visibilityMap.value[node.nodeId]
}

const shownPositions = computed(() => {
  let shown = 0
  return props.schema.children.map((node) => (nodeShown(node) ? shown++ : shown))
})

/** A child's place in its parent's `staggerChildren` cascade: shown siblings before it. */
function cascadeIndex(index: number): number {
  return props.cascade ? (shownPositions.value[index] ?? index) : index
}

// Injected slot fillers from a parent SharedNodeRefRenderer (empty map when not in shared node context)
const sharedNodeSlots = inject<ComputedRef<Record<string, ComponentNode[]>>>(
  'sharedNodeSlots',
  computed(() => ({})),
)

// Editability that the enclosing SharedNodeRefRenderer received from above
// (provided by SharedNodeRefRenderer). Only used by the ComponentSlot filler
// branch — slot fillers are user content on the ref instance, so they follow
// THIS injection rather than the SNR-forced `editable=false` of the template
// subtree. Default `false` is safe: outside any SharedNodeRef the ComponentSlot
// filler branch never fires (no fillers to render), so the inject is irrelevant.
const outerEditable = inject<ComputedRef<boolean>>(
  'outerEditable',
  computed(() => false),
)

const childKeys = computed(() => siblingRenderKeys(props.schema.children))

function hasSlotFillers(node: ComponentNode): boolean {
  const slotName = (node.fields?.name as string) || 'default'
  return (sharedNodeSlots.value[slotName]?.length ?? 0) > 0
}

function getSlotFillers(node: ComponentNode): ComponentNode[] {
  const slotName = (node.fields?.name as string) || 'default'
  return sharedNodeSlots.value[slotName] ?? []
}

// Generalized slot support — detect from data (children with .slot property)
function hasSlottedChildren(node: ComponentNode): boolean {
  return (node.children ?? []).some((c) => c.slot)
}

function groupChildrenBySlot(node: ComponentNode): Record<string, ComponentNode[]> {
  const map: Record<string, ComponentNode[]> = {}
  for (const child of node.children ?? []) {
    const slot = child.slot || 'default'
    if (!map[slot]) map[slot] = []
    map[slot]!.push(child)
  }
  return map
}
</script>
